import { Link } from "react-router-dom";
import { CheckCircle, Clock, Eye } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { getStoredAccountType } from "../auth";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useDisputes } from "../hooks/useDisputes";
import { cn } from "@/shared/lib/cn";

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

const statusBadgeClass = {
  inProgress: "bg-accent-soft text-accent-foreground",
  resolved: "bg-accent/15 text-accent",
  rejected: "bg-danger-soft text-danger",
  closed: "bg-card-muted text-muted-foreground",
} as const;

export function InProgressDisputesPage() {
  const { t } = useTranslation();
  const listLabels = t.safecheck.disputes.list;
  const labels = t.safecheck.disputes.inProgress;
  const { disputes: pendingDisputes, loading } = useDisputes({ status: "inProgress" });

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[{ label: t.safecheck.nav.inProgressDisputes }]}
      />

      <div className="theme-card mt-4 p-5 md:p-6">
        <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Clock size={16} className="text-muted-foreground" />
          {labels.cardTitle}
        </div>

        {loading ? (
          <p className="text-sm text-muted">Chargement…</p>
        ) : pendingDisputes.length === 0 ? (
          <>
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-accent/20 bg-accent-soft px-4 py-3">
              <CheckCircle size={18} className="mt-0.5 shrink-0 text-accent-foreground" />
              <p className="text-sm leading-relaxed text-accent-foreground">{labels.emptySuccess}</p>
            </div>
            <Link
              to="/dashboard/disputes"
              className="inline-flex rounded-lg bg-muted-foreground px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-muted-foreground/90"
            >
              {labels.viewAll}
            </Link>
          </>
        ) : (
          <div className="overflow-x-auto">
            <table className="safecheck-table w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-border bg-card-muted/50">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {listLabels.columns.id}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {listLabels.columns.date}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {listLabels.columns.company}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {listLabels.columns.status}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {listLabels.columns.actions}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingDisputes.map((dispute, index) => (
                  <tr
                    key={dispute.id}
                    className={cn(
                      "border-b border-border last:border-0 hover:bg-card-muted/30",
                      index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                    )}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {dispute.reference}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{dispute.createdAt}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{dispute.company}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                          statusBadgeClass[dispute.status],
                        )}
                      >
                        {listLabels.statuses[dispute.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/disputes/${dispute.id}`}
                        aria-label={listLabels.actions.view}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:border-primary hover:bg-primary-soft"
                      >
                        <Eye size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
